import classNames from 'classnames';
import React, { useMemo } from 'react';
import type { Node } from 'ricos-schema';
import type { Helpers, SEOSettings } from 'ricos-types';
import { Image as ImageComp } from 'wix-rich-content-ui-components';

import styles from '../../statics/styles/image-viewer.rtlignore.scss';
import { RICOS_DEFAULTS } from '../consts';
import ExpandIcon from '../icons/expand';
import type { ImagePluginViewerConfig } from '../types';
import type {
  ImageClickActionConfig,
  ImageClickActionHandle,
} from './get-image-click-action-config';
import { ImageContainer } from './image-container';
import { useCustomWidthStyle } from './use-custom-width-style';
import { useFallbackWidthStyle } from './use-fallback-width-style';
import { useImageDimensions } from './use-image-dimensions';
import { useImageSocialAttributes } from './use-image-social-attributes';

type ImageViewerProps = {
  node: Node;
  isMobile: boolean;
  imageClickActionConfig: ImageClickActionConfig;
  shouldLazyLoadImages: boolean;
  captionComponent: React.ReactNode;
  helpers?: Helpers;
  config?: ImagePluginViewerConfig;
  expand?: ImageClickActionHandle;
  className?: string;
  dataUrl?: string;
  seoMode?: SEOSettings;
};

export const Image = ({
  dataUrl,
  node,
  className,
  config,
  helpers,
  isMobile,
  seoMode,
  imageClickActionConfig,
  expand,
  captionComponent,
  shouldLazyLoadImages,
}: ImageViewerProps) => {
  const containerData = node.imageData?.containerData || RICOS_DEFAULTS.containerData;
  const { image, altText } = node.imageData || {};
  const { src } = image || {};

  const disableDownload =
    node.imageData?.disableDownload === true
      ? (e: React.MouseEvent) => e.preventDefault()
      : undefined;

  const imageSource = useMemo(() => {
    if (dataUrl) {
      return dataUrl;
    }

    const { url, id } = src || {};
    if (url) {
      return url;
    }
    const { getImageUrl } = helpers ?? {};

    if (getImageUrl && id) {
      return getImageUrl({ file_name: id });
    }

    return id;
  }, [
    dataUrl,
    node.imageData?.image?.src?.url,
    node.imageData?.image?.src?.id,
    helpers?.getImageUrl,
  ]);

  const { width, height } = useImageDimensions({
    src: imageSource,
    containerData,
    image,
  });
  const fallbackWidthStyle = useFallbackWidthStyle({ containerData, isMobile });
  const customWidthStyle = useCustomWidthStyle({ containerData, isMobile, width });
  const socialAttributes = useImageSocialAttributes({
    config,
    src,
    width: image?.width,
    height: image?.height,
  });

  const doDimensionsExist = width !== undefined && height !== undefined;

  return (
    <ImageContainer {...imageClickActionConfig} disableDownload={disableDownload}>
      {doDimensionsExist && imageSource && (
        <ImageComp
          id={node.imageData?.image?.src?.id ?? imageSource}
          containerId={node.id}
          displayMode="fill"
          width={width}
          height={height}
          uri={node.imageData?.image?.src?.id ?? imageSource}
          alt={altText || ''}
          containerProps={{
            className: classNames(styles.imageWrapper, styles.imageRatio, className, {
              [styles.pointer]: !!expand,
            }),
            containerId: node.id,
            style: {
              ['--dim-height' as string]: height,
              ['--dim-width' as string]: width,
              ...fallbackWidthStyle,
              ...customWidthStyle,
            },
          }}
          imageStyles={{
            maxWidth: '100%',
          }}
          socialAttrs={socialAttributes}
          isSEOBot={!!seoMode}
          {...(shouldLazyLoadImages
            ? {
                placeholderTransition: seoMode ? undefined : 'blur',
                shouldUseLQIP: config?.disableTransition !== false,
              }
            : {})}
        />
      )}
      {!!expand && (
        <div
          className={styles.expandContainer}
          onClick={expand}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              expand(e);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <ExpandIcon className={styles.expandIcon} />
        </div>
      )}
      {captionComponent}
    </ImageContainer>
  );
};
